<script setup>
import { onMounted, ref, inject, watch, useTemplateRef, nextTick } from 'vue'
import tinymce from 'tinymce'
import 'tinymce/themes/silver/theme'
import 'tinymce/models/dom/model'
import 'tinymce/icons/default/icons'
import 'tinymce/plugins/image'
import 'tinymce/plugins/link'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/table'
import 'tinymce/plugins/emoticons'
import 'tinymce/skins/content/default/content'
import 'tinymce/skins/content/dark/content'
import 'tinymce/skins/ui/oxide/skin'
import 'tinymce/skins/ui/oxide/content'
import 'tinymce/skins/ui/oxide-dark/skin'
import 'tinymce/skins/ui/oxide-dark/content'

const model = defineModel()
const props = defineProps({
  height: [String, null],
  disabled: [Boolean, null],
})
const editorRef = useTemplateRef('editor-area')
const editorInstance = ref(null)
const editing = inject('form:editable')

onMounted(init)

watch(editing, toggleEdit, { immediate: true, flush: 'post' })
watch(() => props.disabled, toggleEdit, { immediate: true, flush: 'post' })
watch(model, setContent, { immediate: true })

async function init() {
  if (editorInstance.value) {
    editorInstance.value.destroy(true)
  }

  const cfg = {
    target: editorRef.value,
    license_key: 'gpl',
    disabled: true,
    statusbar: true,
    resize: true,
    elementpath: false,
    menubar: false,
    branding: false,
    plugins: 'image link lists table emoticons',
    toolbar:
      'undo redo | blocks | bold italic underline forecolor backcolor removeformat | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image table emoticons',
    image_title: true,
    automatic_uploads: true,
    emoticons_database_url: '/emojis.js',

    file_picker_types: 'image',
    file_picker_callback: cb => {
      const input = document.createElement('input')
      input.setAttribute('type', 'file')
      input.setAttribute('accept', 'image/*')

      input.addEventListener('change', e => {
        const file = e.target.files[0]

        const reader = new FileReader()
        reader.addEventListener('load', () => {
          const id = 'blobid' + new Date().getTime()
          const blobCache = tinymce.activeEditor.editorUpload.blobCache
          const base64 = reader.result.split(',')[1]
          const blobInfo = blobCache.create(id, file, base64)
          blobCache.add(blobInfo)
          cb(blobInfo.blobUri(), { title: file.name })
        })
        reader.readAsDataURL(file)
      })

      input.click()
    },
    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:16px }',
    content_css: 'dark',
    skin: 'oxide-dark',
  }
  if (props.height) {
    cfg.height = props.height
  }

  try {
    const [instance] = await tinymce.init(cfg)
    disableBrokenResizeHandler(instance)
    editorInstance.value = instance
    instance.on('Change', onChange)
    nextTick(() => toggleEdit(editing.value))
  } catch (error) {
    console.error('Ошибка при инициализации TinyMCE:', error)
  }
}

// prevent NS_ERROR_UNEXPECTED
function disableBrokenResizeHandler(editor) {
  const events = ['ResizeWindow', 'ResizeEditor', 'ResizeContent']
  for (const evt of events) {
    editor.off(evt)
  }
  const safeResizeHandler = () => {
    try {
      editor.getWin()?.innerWidth
    } catch (e) {
      console.warn('Safe resize handler caught error:', e)
    }
  }
  events.forEach(evt => {
    editor.on(evt, safeResizeHandler)
  })
}

function onChange() {
  if (editorInstance.value) {
    model.value = editorInstance.value.getContent()
  }
}

function setContent(mv) {
  const ei = editorInstance.value

  if (!(ei && ei.initialized)) {
    return
  }

  // clear text in editor
  if (!mv) {
    ei.setContent('')
    return
  }

  // prevent re-setting on input
  if (mv === ei.getContent()) {
    return
  }
  ei.setContent(mv)
}

function toggleEdit(isEditable) {
  const ei = editorInstance.value
  if (!ei) {
    return
  }
  let disabled = true
  if (props.disabled) {
    disabled = true
  } else {
    disabled = !isEditable
  }
  ei.options.set('disabled', disabled)
}
</script>
<template>
  <div class="editor-wrapper">
    <textarea v-model="model" ref="editor-area" />
  </div>
</template>
